import React from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useEstimationUnit } from '../analytics/estimation/estimationUnit';
import { WorkLogEntry } from '../../shared/types';
import {
  aggregatePrecomputedByIssueType,
  aggregateWorklogByIssueType,
  getTypeColor,
  isBugSubtaskStripeLabel,
  type PrecomputedTypeRow,
  typeSwatchStyle,
} from './taskTypeMeta';

export type { PrecomputedTypeRow };

interface TaskTypeDistributionChartProps {
  data: WorkLogEntry[];
  /** Server aggregates; when present, pie/hours panels use this instead of scanning `data`. */
  precomputedByType?: PrecomputedTypeRow[];
}

interface TaskTypeData {
  name: string;
  value: number;
  percentage: number;
}

export const TaskTypeDistributionChart: React.FC<TaskTypeDistributionChartProps> = ({
  data,
  precomputedByType,
}) => {
  const { toUnitValue, suffix, unit } = useEstimationUnit();
  const typeData = precomputedByType?.length
    ? aggregatePrecomputedByIssueType(precomputedByType)
    : aggregateWorklogByIssueType(data);

  const totalIssues = Object.values(typeData).reduce((s, r) => s + r.issueCount, 0);
  const totalRows = Object.values(typeData).reduce((s, r) => s + r.rowCount, 0);

  const chartData: TaskTypeData[] = Object.entries(typeData)
    .map(([name, agg]) => ({
      name,
      value: agg.issueCount,
      percentage: totalIssues > 0 ? (agg.issueCount / totalIssues) * 100 : 0,
    }))
    .sort((a, b) => b.value - a.value);

  const hoursData = Object.entries(typeData)
    .map(([name, agg]) => ({
      name,
      hours: agg.hours,
      issueCount: agg.issueCount,
    }))
    .sort((a, b) => b.hours - a.hours);

  interface TooltipPayloadItem {
    payload?: TaskTypeData;
  }

  interface TooltipProps {
    active?: boolean;
    payload?: TooltipPayloadItem[];
  }

  interface LabelProps {
    percentage?: number;
  }

  const CustomTooltip = ({ active, payload }: TooltipProps) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload as TaskTypeData;
      return (
        <div
          style={{
            backgroundColor: 'var(--color-bg-elevated)',
            padding: '12px',
            border: '1px solid var(--color-border-strong)',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
          }}
        >
          <p style={{ margin: '0 0 4px 0', fontWeight: 'bold', color: 'var(--color-text)' }}>
            {item.name}
          </p>
          <p style={{ margin: '4px 0', color: 'var(--color-text-secondary)' }}>
            Unique issues: {item.value.toLocaleString('en-US')}
          </p>
          <p style={{ margin: '4px 0', color: 'var(--color-text-secondary)', fontWeight: 'bold' }}>
            Percentage: {item.percentage.toFixed(1)}%
          </p>
        </div>
      );
    }
    return null;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderCustomLegend = (props: any) => {
    const { payload } = props;
    if (!payload || !Array.isArray(payload)) return null;
    return (
      <ul
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          listStyle: 'none',
          padding: 0,
          margin: '20px 0 0 0',
          gap: '16px',
        }}
      >
        {payload.map((entry: any, index: number) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const item = (entry.payload || entry) as TaskTypeData;
          const color = getTypeColor(item.name, index);
          return (
            <li
              key={`item-${index}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
              }}
            >
              <span style={typeSwatchStyle(item.name, color, 12)} />
              <span style={{ color: 'var(--color-text)' }}>
                {item.name}: {item.percentage.toFixed(1)}% ({item.value.toLocaleString('en-US')})
              </span>
            </li>
          );
        })}
      </ul>
    );
  };

  if (chartData.length === 0) {
    return (
      <div
        className="dashboard-card"
        style={{
          height: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <p style={{ color: 'var(--color-text-secondary)' }}>No data to display</p>
      </div>
    );
  }

  return (
    <div
      className="dashboard-card"
      title="Unique issues with a worklog in the current Period, grouped by type. Counts are issues, not worklog rows."
    >
      <h3>
        Task Type Distribution
        <span
          style={{
            marginLeft: '0.5rem',
            fontSize: '0.75rem',
            color: 'var(--color-text-secondary)',
            fontWeight: 'normal',
          }}
          title="Pie slices and legend counts are unique issues with worklog, not worklog rows. Hours still sum every worklog entry."
        >
          ℹ️
        </span>
      </h3>
      <div
        style={{
          display: 'flex',
          gap: '24px',
          marginTop: '20px',
          alignItems: 'flex-start',
        }}
      >
        <div style={{ flex: '1 1 60%', height: '400px', overflow: 'visible' }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <defs>
                <pattern
                  id="subtask-stripe"
                  x="0"
                  y="0"
                  width="8"
                  height="8"
                  patternUnits="userSpaceOnUse"
                >
                  <rect width="8" height="8" fill="#ffffff" />
                  <path
                    d="M 0,0 L 8,8 M -2,2 L 2,6 M 6,2 L 10,6"
                    stroke="#4bade8"
                    strokeWidth="1.5"
                  />
                </pattern>
                <pattern
                  id="bug-subtask-stripe-pie"
                  x="0"
                  y="0"
                  width="8"
                  height="8"
                  patternUnits="userSpaceOnUse"
                >
                  <rect width="8" height="8" fill="#ffffff" />
                  <path
                    d="M 0,0 L 8,8 M -2,2 L 2,6 M 6,2 L 10,6"
                    stroke="#e5493a"
                    strokeWidth="1.5"
                  />
                </pattern>
              </defs>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(props: LabelProps) => {
                  const percentage = props.percentage || 0;
                  if (percentage >= 5) {
                    return `${percentage.toFixed(1)}%`;
                  }
                  return '';
                }}
                outerRadius={120}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry, index) => {
                  const fillColor =
                    entry.name === 'Sub-task'
                      ? 'url(#subtask-stripe)'
                      : isBugSubtaskStripeLabel(entry.name)
                        ? 'url(#bug-subtask-stripe-pie)'
                        : getTypeColor(entry.name, index);
                  return <Cell key={`cell-${index}`} fill={fillColor} />;
                })}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend content={renderCustomLegend} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div
          style={{
            flex: '1 1 40%',
            padding: '20px',
            backgroundColor: 'var(--color-bg-surface, #f9fafb)',
            borderRadius: '8px',
            border: '1px solid var(--color-border, #e5e7eb)',
          }}
        >
          <h4
            style={{
              margin: '0 0 16px 0',
              fontSize: '16px',
              fontWeight: '600',
              color: 'var(--color-text, #111827)',
            }}
          >
            Tracked {unit === 'days' ? 'Days' : 'Hours'} by Type
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {hoursData.map((item, index) => {
              const color = getTypeColor(item.name, index);
              return (
                <div
                  key={item.name}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    backgroundColor: 'var(--color-bg-elevated, #ffffff)',
                    borderRadius: '6px',
                    border: '1px solid var(--color-border, #e5e7eb)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={typeSwatchStyle(item.name, color, 16)} />
                    <span
                      style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: 'var(--color-text, #374151)',
                      }}
                    >
                      {item.name} ({item.issueCount.toLocaleString('en-US')}):
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--color-text, #111827)',
                    }}
                  >
                    {toUnitValue(item.hours).toFixed(1)} {suffix}
                  </span>
                </div>
              );
            })}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                backgroundColor: 'var(--color-bg-elevated, #ffffff)',
                borderRadius: '6px',
                border: '1px solid var(--color-border, #e5e7eb)',
                fontWeight: '700',
                borderTop: '2px solid var(--color-border-strong, #9ca3af)',
                marginTop: '4px',
              }}
            >
              <span style={{ fontSize: '14px', color: 'var(--color-text, #111827)' }}>
                Total ({totalIssues.toLocaleString('en-US')} issues):
              </span>
              <span
                style={{ fontSize: '14px', fontWeight: '700', color: 'var(--color-text, #111827)' }}
              >
                {toUnitValue(hoursData.reduce((sum, item) => sum + item.hours, 0)).toFixed(1)}{' '}
                {suffix}
              </span>
            </div>
            <p className="type-count-hint">
              {totalRows.toLocaleString('en-US')} worklog entries across these issues. Total Issues
              Done is the subset still in status Done.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskTypeDistributionChart;
